import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Autocomplete, getFilterOptions } from "@msys/ui";
import {
  Functions as FunctionsIcon,
  Input as InputIcon,
  ReportProblem as ReportProblemIcon,
} from "@mui/icons-material";
import type { FilterOptionsState } from "@mui/material";
import {
  AutocompleteRenderGroupParams,
  Chip,
  IconButton,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { constant, findIndex, intersection, isEqual } from "lodash-es";
import React from "react";
import { Props2 } from "../../../../clients/graphqlTypes.js";
import {
  getEnhancedPropertyLabel,
  isComputedProp,
  isPropMissingValue,
} from "../properties.js";
import {
  ExpressionInputWithVariables_ItemFragment,
  useExpressionInputWithVariablesQuery,
} from "./ExpressionInputWithVariables.generated.js";

export interface VariableInputState {
  expression: string;
  cursorIndex: number | null;
}

export interface PropertyOption {
  value: string;
  key: string;
  label: string;
  itemId: string;
  isComputed: boolean;
  hasValue: boolean;
  group: string;
}

export type ExpressionItemSource = "self" | "parent" | "root";

export function ExpressionInputWithVariables({
  itemId,
  docId,
  projectId,
  expressionError,
  inputState,
  onInputStateChange,
  label,
  placeholder,
  filterOptions,
  autoFocus = false,
  direction,
  allowedSources,
  disabled,
  showSelectedVariableAllowedValues = false,
}: {
  itemId: string;
  docId: string;
  projectId?: string | null;
  expressionError?: Error | null;
  inputState: VariableInputState;
  onInputStateChange: (state: VariableInputState) => void;
  label?: string;
  placeholder?: string;
  filterOptions?: (option: PropertyOption) => boolean;
  autoFocus?: boolean;
  direction: "row" | "column";
  allowedSources?: ExpressionItemSource[];
  disabled?: boolean;
  showSelectedVariableAllowedValues?: boolean;
}) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const expressionFieldRef = React.useRef<HTMLTextAreaElement>(null);

  const [selectedVariable, setSelectedVariable] =
    React.useState<PropertyOption | null>(null);

  const client = useApolloClient();
  const query = useExpressionInputWithVariablesQuery({
    client,
    variables: {
      docId,
      projectId,
    },
  });
  const items = React.useMemo(
    () => getDataOrNull(query.data?.items)?.items ?? [],
    [query.data?.items]
  );
  const self = React.useMemo(
    () => items.find(i => i.id === itemId),
    [itemId, items]
  );

  const propertyOptions = React.useMemo((): PropertyOption[] => {
    const options = items
      .filter(getFilterItemByAllowedSource(allowedSources, self))
      .sort(getSortItems(self))
      .flatMap(item => item.props2.map(getPropToOption(item)));
    return options;
  }, [allowedSources, items, self]);

  const selectedVariableAllowedValues = useSelectedVariableAllowedValues(
    items,
    selectedVariable
  );

  const firstGroupByItem = React.useRef<Map<string, string>>(new Map());

  const filterAutocompleteOptions = React.useMemo(
    () =>
      (
        options: PropertyOption[],
        state: FilterOptionsState<PropertyOption>
      ): PropertyOption[] => {
        const filteredOptions = filterOptionsByLabel(
          ungroupOptions(groupOptions(options)).filter(
            filterOptions || constant(true)
          ),
          state
        );
        const resultedOptions = ungroupOptions(groupOptions(filteredOptions));
        firstGroupByItem.current = new Map(
          resultedOptions
            .filter(
              (o, i, arr) => arr.findIndex(a => a.itemId === o.itemId) === i
            )
            .map(o => [o.itemId, o.group])
        );
        return resultedOptions;
      },
    [filterOptions]
  );

  if (disabled) {
    return (
      <Stack direction={"row"} spacing={1} alignItems={"center"} flex={1}>
        <ExpressionInput
          autoFocus={autoFocus}
          label={label ?? t("Formula", { ns: "QuoteItem" })}
          placeholder={placeholder}
          value={inputState.expression}
          error={Boolean(expressionError)}
          helperText={expressionError?.message}
        />
      </Stack>
    );
  }

  return (
    <Stack
      spacing={1}
      direction={direction}
      alignItems={direction === "row" ? "flex-start" : "stretch"}
    >
      <Stack direction={"row"} spacing={1} alignItems={"center"} flex={1}>
        <ExpressionInput
          inputRef={expressionFieldRef}
          autoFocus={autoFocus}
          label={label ?? t("Formula", { ns: "QuoteItem" })}
          placeholder={placeholder}
          value={inputState.expression}
          onChange={event =>
            onInputStateChange({
              ...inputState,
              expression: event.target.value,
            })
          }
          onBlur={event =>
            onInputStateChange({
              ...inputState,
              cursorIndex: event.target.selectionStart,
            })
          }
          onFocus={event => {
            setTimeout(() => {
              event.target.setSelectionRange(
                inputState.cursorIndex,
                inputState.cursorIndex
              );
            });
          }}
          error={Boolean(expressionError)}
          helperText={expressionError?.message}
        />
      </Stack>
      <Stack flex={1} spacing={1}>
        <Stack direction={"row"} spacing={1} alignItems={"center"} flex={1}>
          <Autocomplete<PropertyOption, false>
            inputLabel={t("Variable for formula", { ns: "QuoteItem" })}
            options={propertyOptions}
            groupBy={getGroupKey}
            renderGroup={getRenderGroup({
              itemId,
              items,
              self,
              groupTitle: t("Group", { ns: "QuoteItem" }),
              notGroupedTitle: t("Non-grouped", { ns: "QuoteItem" }),
              firstGroupByItem,
            })}
            renderOption={renderOption}
            filterOptions={filterAutocompleteOptions}
            value={selectedVariable}
            isOptionEqualToValue={(option, value) => isEqual(option, value)}
            onChange={value => setSelectedVariable(value)}
          />
          <IconButton
            color={"secondary"}
            disabled={!selectedVariable}
            onClick={() => {
              if (!selectedVariable) return;

              const { expression, cursorIndex } = inputState;
              const index = cursorIndex ?? expression.length;

              onInputStateChange({
                expression: `${expression.substring(0, index)}${
                  selectedVariable.value
                }${expression.substring(index)}`,
                cursorIndex: (cursorIndex ?? 0) + selectedVariable.value.length,
              });

              setTimeout(() => {
                expressionFieldRef.current?.focus();
              });
            }}
          >
            <InputIcon />
          </IconButton>
        </Stack>
        {showSelectedVariableAllowedValues && selectedVariableAllowedValues && (
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            flexWrap={"wrap"}
            useFlexGap={true}
          >
            {selectedVariableAllowedValues.values.map(value => (
              <Chip
                key={value.toString()}
                label={value.toString()}
                variant="filled"
                onClick={() => {
                  const { expression, cursorIndex } = inputState;
                  const index = cursorIndex ?? expression.length;

                  onInputStateChange({
                    expression: `${expression.substring(0, index)}${value.toString()}${expression.substring(index)}`,
                    cursorIndex: (cursorIndex ?? 0) + value.toString().length,
                  });

                  setTimeout(() => {
                    expressionFieldRef.current?.focus();
                  });
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export const getFilterItemByAllowedSource =
  (
    allowedSources: ExpressionItemSource[] | undefined,
    self: ExpressionInputWithVariables_ItemFragment | undefined
  ) =>
  (item: ExpressionInputWithVariables_ItemFragment) => {
    if (!allowedSources) return true;
    const sources: ExpressionItemSource[] = [];
    if (item.id === self?.id) sources.push("self");
    if (item.id === self?.parentId) sources.push("parent");
    if (item.isRootItem) sources.push("root");
    return intersection(sources, allowedSources).length > 0;
  };

export const useSelectedVariableAllowedValues = (
  items: ExpressionInputWithVariables_ItemFragment[],
  selectedVariable: PropertyOption | null
) => {
  return React.useMemo(() => {
    if (!selectedVariable) return null;

    const item = items.find(i => i.id === selectedVariable.itemId);
    if (!item) return null;

    const prop = item.props2.find(p => p.key === selectedVariable.key);
    if (!prop) return null;

    if (prop.__typename === "Props2Bool") {
      return {
        values: [true, false],
        allowMultiple: false,
      };
    } else if (prop.__typename === "Props2Number" && prop.allowedValuesNumber) {
      return {
        values: prop.allowedValuesNumber.map(
          allowedValue => allowedValue.allowedNumber
        ),
        allowMultiple: false,
      };
    } else if (
      prop.__typename === "Props2NumberArray" &&
      prop.allowedValuesNumber
    ) {
      return {
        values: prop.allowedValuesNumber.map(
          allowedValue => allowedValue.allowedNumber
        ),
        allowMultiple: true,
      };
    } else if (prop.__typename === "Props2Text" && prop.allowedValuesText) {
      return {
        values: prop.allowedValuesText.map(
          allowedValue => allowedValue.allowedText
        ),
        allowMultiple: false,
      };
    } else if (
      prop.__typename === "Props2TextArray" &&
      prop.allowedValuesText
    ) {
      return {
        values: prop.allowedValuesText.map(
          allowedValue => allowedValue.allowedText
        ),
        allowMultiple: true,
      };
    }
    return null;
  }, [items, selectedVariable]);
};

export const getPropToOption =
  (item: ExpressionInputWithVariables_ItemFragment) =>
  (prop: Props2): PropertyOption => ({
    itemId: item.id,
    value: `prop($item_${item.id.replaceAll("-", "_")}, "${prop.key}")`,
    key: prop.key,
    label: getEnhancedPropertyLabel(prop, false),
    isComputed: isComputedProp(prop),
    hasValue: !isPropMissingValue(prop),
    group: prop.group,
  });

export const getSortItems =
  (self: ExpressionInputWithVariables_ItemFragment | undefined) =>
  (
    item1: ExpressionInputWithVariables_ItemFragment,
    item2: ExpressionInputWithVariables_ItemFragment
  ) => {
    if (item1.id === self?.id) return -1;
    if (item2.id === self?.id) return 1;
    if (item1.id === self?.parentId) return -1;
    if (item2.id === self?.parentId) return 1;
    if (item1.isRootItem) return -1;
    if (item2.isRootItem) return 1;
    return 0;
  };

export const getGroupKey = (option: PropertyOption) =>
  `${option.itemId}_${option.group}`;

export const getRenderGroup =
  ({
    itemId,
    self,
    items,
    groupTitle,
    notGroupedTitle,
    firstGroupByItem,
  }: {
    itemId: string;
    self: ExpressionInputWithVariables_ItemFragment | undefined;
    items: ExpressionInputWithVariables_ItemFragment[];
    groupTitle: string;
    notGroupedTitle: string;
    firstGroupByItem: React.MutableRefObject<Map<string, string>>;
  }) =>
  (params: AutocompleteRenderGroupParams) => {
    const [groupItemId, ...groupRest] = params.group.split("_");
    const group = groupRest.join("_");
    const item = items.find(item => item.id === groupItemId);
    const isFirstGroupForItem =
      firstGroupByItem.current.get(groupItemId) === group;
    const pseudoReferences = [];
    if (groupItemId === itemId) pseudoReferences.push("self");
    if (item?.id === self?.parentId) pseudoReferences.push("parent");
    if (item?.isRootItem) pseudoReferences.push("root");
    return (
      <li key={params.group}>
        {isFirstGroupForItem && (
          <GroupHeader direction="row" spacing={1}>
            <Typography fontWeight={"bold"}>
              {`${item?.pathForPdf} ${item?.title}`.trim()}
            </Typography>
            {pseudoReferences.length > 0 ? (
              <Typography
                color={"gray"}
              >{`(${pseudoReferences.join(", ")})`}</Typography>
            ) : null}
          </GroupHeader>
        )}
        <GroupSubHeader direction="row" spacing={1}>
          <Typography
            color={"secondary"}
          >{`${groupTitle}: ${group || notGroupedTitle}`}</Typography>
        </GroupSubHeader>
        <GroupItems>{params.children}</GroupItems>
      </li>
    );
  };

export const renderOption = (
  { key, ...props }: React.HTMLAttributes<HTMLLIElement> & { key: any },
  option: PropertyOption
) => {
  return (
    <Stack key={key} component={"li"} direction={"row"} spacing={1} {...props}>
      <Typography>{option.label}</Typography>
      {option.isComputed && (
        <Tooltip title="Computed" arrow placement="top">
          <FunctionsIcon fontSize="small" color="secondary" />
        </Tooltip>
      )}
      {!option.hasValue && (
        <Tooltip title="Missing value" arrow placement="top">
          <ReportProblemIcon fontSize="small" color="error" />
        </Tooltip>
      )}
    </Stack>
  );
};

export const filterOptionsByLabel = getFilterOptions(["label"]);

export const GroupHeader = styled(Stack)(({ theme }) => ({
  position: "sticky",
  top: 0,
  padding: "4px 16px",
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.default,
}));

export const GroupSubHeader = styled(Stack)(({ theme }) => ({
  padding: "4px 16px",
  paddingLeft: "24px !important",
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.default,
}));

export const GroupItems = styled("ul")({
  padding: 0,
  "& > li": {
    paddingLeft: "32px !important",
    paddingTop: "4px !important",
    paddingBottom: "4px !important",
  },
});

export function ExpressionInput({
  ...props
}: React.ComponentProps<typeof TextField>) {
  return <TextField {...props} multiline />;
}

export type OptionWithGroup = { group: string };
export type OptionsGrouped<O extends OptionWithGroup = OptionWithGroup> = {
  key: string;
  options: O[];
};

export function groupOptions<O extends OptionWithGroup>(
  options: O[]
): OptionsGrouped<O>[] {
  return options.reduce((acc, prop) => {
    const groupIndex = findIndex(acc, g => g.key === prop.group);
    if (groupIndex >= 0) {
      acc[groupIndex].options.push(prop);
    } else {
      acc.push({ key: prop.group, options: [prop] });
    }
    return acc;
  }, [] as OptionsGrouped<O>[]);
}

export function ungroupOptions<O extends OptionWithGroup>(
  groups: OptionsGrouped<O>[]
): O[] {
  return groups.map(g => g.options).flat(1);
}
